// @flow
import React, { useState, useCallback } from 'react';
import styled from 'styled-components';
import Button, { LoadingButton } from '@atlaskit/button';
import Modal, { ModalBody, ModalFooter, ModalHeader, ModalTitle, ModalTransition } from '@atlaskit/modal-dialog';
import SectionMessage from '@atlaskit/section-message';
import { withApollo, WithApolloClient } from 'react-apollo';
import VirtualComponentSelect, { parseVirtualComponentToOption } from '../select/VirtualComponentSelect';

const Wrapper = styled.div`
    p.description {
        margin-bottom: 7px;
    }
    div.select-wrapper {
        margin-top: 14px;
    }
`;

type Props = {
    setIsOpen: (isOpen: boolean) => void,
    confirm: Function,
    loading: boolean,
    duplicates: Array,
    virtualComponent: Object,
    componentParentsDefaultOptions?: OptionsType[],
};

const MergeComponentModal = ({
    setIsOpen,
    confirm,
    loading,
    duplicates,
    virtualComponent,
    componentParentsDefaultOptions,

    /* props from compose */
    client,

    /* rest */
    ...props
}: WithApolloClient<Props>) => {
    const [submitting, setSubmitting] = useState(false);
    const [selected, setSelected] = useState(null);

    const submit = useCallback(async () => {
        try {
            setSubmitting(true);
            await confirm(virtualComponent.id, selected?.value);
        } finally {
            setSubmitting(false);
        }
    }, [confirm, selected?.value, virtualComponent.id]);

    return (
        <ModalTransition>
            <Modal {...props} shouldScrollInViewport onClose={() => setIsOpen(false)}>
                <ModalHeader>
                    <ModalTitle>Merge &quot;{virtualComponent.name}&quot; into another component</ModalTitle>
                </ModalHeader>
                <ModalBody>
                    <Wrapper>
                        <p className="description">
                            All of the attributes from the target component will be set in this component.
                        </p>

                        {duplicates && selected && (
                            <SectionMessage appearance="error">
                                <p>{`Component can't be merged.`}</p>
                                <p>
                                    <span>
                                        Component &quot;{selected.label}&quot; already exists in project
                                        {duplicates.length > 1 ? 's ' : ' '}
                                    </span>
                                    {duplicates.map((projectKey, i) => (
                                        <span key={projectKey}>
                                            {i ? ', ' : ''}
                                            <a
                                                key={projectKey}
                                                style={{ outline: 'none' }}
                                                onClick={() =>
                                                    window.PlatformAdapter.openProjectUrl(
                                                        projectKey,
                                                        undefined,
                                                        `filter=${selected.label}`,
                                                    )
                                                }
                                            >
                                                {projectKey}
                                            </a>
                                        </span>
                                    ))}
                                    .
                                </p>
                                <p>Change the component name to enable merge.</p>
                            </SectionMessage>
                        )}

                        <div className="select-wrapper">
                            <VirtualComponentSelect
                                isDisabled={submitting}
                                autoFocus
                                value={selected}
                                onChange={(newValue) => setSelected(newValue)}
                                defaultOptions={
                                    componentParentsDefaultOptions?.length
                                        ? componentParentsDefaultOptions
                                              ?.filter(({ id }) => id !== virtualComponent.id)
                                              .map(parseVirtualComponentToOption)
                                        : true
                                }
                                excludeComponentIds={[virtualComponent.id]}
                            />
                        </div>
                    </Wrapper>
                </ModalBody>

                <ModalFooter>
                    <LoadingButton
                        isDisabled={!selected}
                        isLoading={submitting}
                        appearance="primary"
                        id="merge"
                        onClick={submit}
                    >
                        Merge
                    </LoadingButton>
                    <Button isDisabled={submitting} id="cancel" onClick={() => setIsOpen(false)}>
                        Cancel
                    </Button>
                </ModalFooter>
            </Modal>
        </ModalTransition>
    );
};
export default withApollo(MergeComponentModal);
